import { lazy, Suspense, useEffect, useRef } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigation, useSearchParams } from 'react-router-dom'

import NotFound from 'src/components/atoms/notFound'
import LoadingApp from 'src/components/molecules/loadingApp'
import GlobalDrawerRenderer from 'src/components/organisms/globalDrawerRenderer/globalDrawerRenderer'
import GlobalModalRenderer from 'src/components/organisms/globalModalRenderer/globalModalRenderer'
import useAPIRequest from 'src/hooks/useAPIRequest'
import useCountersChanged from 'src/hooks/useCountersChanged'
import { routes } from 'src/routes/routes'
import { UIActions, useUIDispatch } from 'src/state/uiState'
import { useUser } from 'src/state/userState'
import { drawerActions } from 'src/store/drawer'
import { modalActions } from 'src/store/modal'

import apiClient from '../../apiClient'

import { Layout } from 'antd'

const AppHeader = lazy(() => import(/* webpackChunkName: 'app-header' */ '../../components/AppHeader'))

const headerLessPages = [routes.DEALER_SELECTOR, routes.BUSINESS_UNIT_SELECTOR, routes.BUSINESS_UNIT_BRAND_SELECTOR]

interface Props {
    appRoutes: Array<React.ReactElement<unknown>>
    confirmingDelivery?: boolean
}

const Root = (props: Props) => {
    const { appRoutes, confirmingDelivery } = props

    const location = useLocation()
    const navigation = useNavigation()
    const [searchParams] = useSearchParams()

    const uiDispatch = useUIDispatch()

    const { dealer, businessUnit, businessUnitBrand } = useUser()

    const { callAPI: getApplications, result: applications } = useAPIRequest(apiClient.getApplications)

    const requiredFields = [dealer?.name, businessUnit?.internalName, businessUnitBrand?.name]

    useEffect(() => {
        const allFieldsValid = requiredFields.every((field) => field?.trim())

        // Call getApplication() only when we have obtained a FULL access token
        if (allFieldsValid && location.pathname !== routes.BUSINESS_UNIT_BRAND_SELECTOR) {
            getApplications().catch(() => {
                // Error is already handled by useAPIRequest hook
            })
        }
    }, requiredFields)

    useEffect(() => {
        if (applications?.length) {
            uiDispatch({ type: UIActions.STORE_APPLICATIONS, payload: applications })
        }
    }, [applications])

    // Only the counters of the expert module on screen (/meta itself is only loaded at startup)
    const { callAPI: getExpertCounters } = useAPIRequest(apiClient.getExpertCounters)

    const refreshExpertCounters = async (module: string) => {
        if (!requiredFields.every((field) => field?.trim())) return
        if (module !== 'trade-in' && module !== 'manager') return

        const counters = await getExpertCounters(module)
        for (const appCounters of counters ?? []) {
            uiDispatch({ type: UIActions.UPDATE_APP_COUNTERS, payload: appCounters })
        }
    }

    // Only the counters of the sales module on screen
    const { callAPI: getSalesCounters } = useAPIRequest(apiClient.getSalesCounters)

    const refreshSalesCounters = async (module: string) => {
        if (!requiredFields.every((field) => field?.trim())) return
        if (module !== 'publishing') return

        // Only the item group on screen: /sales/{module}/{itemGroup}/...
        const itemGroup = location.pathname.split('/')[3]
        if (itemGroup !== 'publications' && itemGroup !== 'sales_processes' && itemGroup !== 'physical_process') {
            return
        }

        const counters = await getSalesCounters({ module, itemGroup })
        for (const appCounters of counters ?? []) {
            uiDispatch({ type: UIActions.UPDATE_APP_COUNTERS, payload: appCounters })
        }
    }

    useCountersChanged({
        expert: refreshExpertCounters,
        sales: refreshSalesCounters,
    })

    useEffect(() => {
        if (navigation.state === 'loading' || navigation.state === 'submitting') {
            drawerActions.clearAllDrawers()
            modalActions.clearAllModals()
        }
    }, [navigation.state])

    const prevSelectedEntityRef = useRef(searchParams.get('selectedEntity'))
    useEffect(() => {
        const currentSelectedEntity = searchParams.get('selectedEntity')
        // We previously cleared on any URL change without selectedEntity, which closed
        // unrelated drawers (e.g. filters) on every search-param update. Only act on the
        // transition from set -> unset, which represents an explicit deselect/back-nav.
        if (prevSelectedEntityRef.current && !currentSelectedEntity) {
            drawerActions.clearAllDrawers()
            modalActions.clearAllModals()
        }
        prevSelectedEntityRef.current = currentSelectedEntity
    }, [location.pathname, searchParams])
    // Enable pusher logging - don't include this in production
    // Pusher.logToConsole = true;

    //init pusher, this is just the setup + test and will be expanded later
    //const pusher = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, { cluster: 'eu' })

    // const channel = pusher.subscribe('my-channel')
    // // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // channel.bind('my-event', function (data: any) {
    //     alert(JSON.stringify(data))
    // })

    const showHeader =
        !headerLessPages.includes(location.pathname) &&
        !confirmingDelivery &&
        !location.pathname.startsWith('/physical-process-workshop') //crappy way to prevent a navigation header for this specific page that is used for physical process followup in the workshop through QR-codes
    return (
        <Layout data-testid="root" className="h-screen w-screen overflow-hidden bg-white">
            {showHeader ? <AppHeader /> : null}
            <Suspense fallback={<LoadingApp />}>
                <Routes>
                    {appRoutes}
                    <Route index element={<Navigate to={routes.HOME} />} />
                    <Route path={'*'} element={<NotFound />} />
                </Routes>
            </Suspense>
            <GlobalModalRenderer />
            <GlobalDrawerRenderer />
        </Layout>
    )
}

export default Root
